import { useState, useEffect, useMemo, useCallback } from "react";
import { Sidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import {
  Box,
  Avatar,
  Chip,
  IconButton,
  Typography,
  Tooltip,
  Divider,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Drawer,
  alpha,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";

import { tokens } from "../../theme";
import { useAuth } from "../../AuthContext";
import { useTaskApi } from "../taskmanager/taskApi";

import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import CloseIcon from "@mui/icons-material/Close";
import MenuOpenOutlinedIcon from "@mui/icons-material/MenuOpenOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import AddIcon from "@mui/icons-material/Add";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

const STAFF_IMAGE_BASE = "https://mindsinaction.com.na/assets/media/pictures/";

// view < edit < full. Mirrors taskPermissionAtLeast() in task_functions.php.
const PERMISSION_META = {
  view: { label: "View only", icon: VisibilityOutlinedIcon },
  edit: { label: "View & edit", icon: EditOutlinedIcon },
  full: { label: "Full rights", icon: ShieldOutlinedIcon },
};

const initials = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
};

/**
 * Folders are stored flat with a parent_id (0 = root), so the tree is
 * assembled here rather than with a recursive CTE - older MariaDB does not
 * have them, and one person's folder list is small enough that it does not
 * matter.
 */
const buildTree = (folders, parentId = 0, depth = 0, guard = 0) => {
  if (guard > 10) return []; // a malformed parent chain must not hang the UI
  return folders
    .filter((f) => Number(f.parent_id) === Number(parentId))
    .sort(
      (a, b) =>
        Number(a.sort_order) - Number(b.sort_order) ||
        String(a.name).localeCompare(String(b.name))
    )
    .map((f) => ({
      ...f,
      depth,
      children: buildTree(folders, f.id, depth + 1, guard + 1),
    }));
};

const TaskSideBar = ({ onShareAccount, onCreateFolder }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { userID } = useAuth();
  // Task work runs on the generic CRUD helpers, not on custom PHP actions.
  const callAction = useTaskApi();

  const [searchParams] = useSearchParams();
  const selectedAccount = searchParams.get("account") || String(userID || "");
  const selectedFolder = searchParams.get("folder") || "";

  const [isCollapsed, setIsCollapsed] = useState(false);
  // Below md the sidebar becomes a drawer - there is no room for a column.
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [loadingFolders, setLoadingFolders] = useState(false);

  const ui = useMemo(() => {
    const accent = colors.teal[500];
    return {
      accent,
      accent2: colors.newTeal[500],
      surface: isDark ? colors.dark[700] : colors.white[900],
      surfaceAlt: isDark ? colors.dark[600] : colors.white[600],
      border: isDark
        ? alpha(colors.white[500], 0.09)
        : alpha(colors.dark[500], 0.1),
      textPrimary: colors.primary[100],
      textSecondary: isDark ? colors.gray[400] : colors.gray[300],
      gradient: `linear-gradient(135deg, ${accent} 0%, ${colors.newTeal[500]} 100%)`,
    };
  }, [colors, isDark]);

  /* ---------------- Accounts: mine, then shared with me ---------------- */
  const loadAccounts = useCallback(async () => {
    setLoadingAccounts(true);
    const res = await callAction("proccess.php", "getTaskAccounts");
    if (res.success && Array.isArray(res.data)) {
      setAccounts(res.data);
    }
    setLoadingAccounts(false);
  }, [callAction]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  /* ---------------- Folders for whichever account is open -------------- */
  const loadFolders = useCallback(async () => {
    if (!selectedAccount) return;
    setLoadingFolders(true);
    // folders_only skips the tasks/subtasks/members/progress payload - the
    // board fetches that itself.
    const res = await callAction("proccess.php", "getTaskWorkspace", {
      owner_id: selectedAccount,
      folders_only: 1,
    });
    setFolders(
      res.success && Array.isArray(res.data?.folders) ? res.data.folders : []
    );
    setLoadingFolders(false);
  }, [callAction, selectedAccount]);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  /* ---------------- Who I have given access to ------------------------ */

  const [grantees, setGrantees] = useState([]);
  const [loadingGrantees, setLoadingGrantees] = useState(true);

  const loadGrantees = useCallback(async () => {
    setLoadingGrantees(true);
    const res = await callAction("proccess.php", "getTaskAccessList", {
      owner_id: userID,
    });
    setGrantees(res.success && Array.isArray(res.data) ? res.data : []);
    setLoadingGrantees(false);
  }, [callAction, userID]);

  useEffect(() => {
    loadGrantees();
  }, [loadGrantees]);

  /**
   * Granting and revoking happen in the board's dialog, which cannot reach
   * into this component. It fires this event on success so the list here
   * does not sit stale until the next refresh.
   */
  useEffect(() => {
    const onChange = () => loadGrantees();
    window.addEventListener("task-access-changed", onChange);
    return () => window.removeEventListener("task-access-changed", onChange);
  }, [loadGrantees]);

  const tree = useMemo(() => buildTree(folders), [folders]);

  const myAccount = accounts.find((a) => Number(a.is_own) === 1);
  const sharedAccounts = accounts.filter((a) => Number(a.is_own) !== 1);

  const currentAccount = accounts.find(
    (a) => String(a.owner_id) === String(selectedAccount)
  );
  const canCreateFolder = (currentAccount?.permission || "view") === "full";

  /* ---------------- Selection lives in the URL ------------------------- */
  // So a refresh, a bookmark or a pasted link all land in the same place,
  // and the board can read it without extra context plumbing.
  // navigate, not setSearchParams: a single task has its own page now, so
  // changing only the query string would leave you on that task while the
  // sidebar claimed you had switched accounts.
  const selectAccount = (ownerId) => {
    setMobileOpen(false);
    navigate(`/tasks/dashboard?account=${encodeURIComponent(String(ownerId))}`);
  };

  const selectFolder = (folderId) => {
    setMobileOpen(false);
    const query = new URLSearchParams({ account: String(selectedAccount) });
    if (folderId) query.set("folder", String(folderId));
    navigate(`/tasks/dashboard?${query.toString()}`);
  };

  // TaskFrame renders the sidebar and the board as siblings and passes no
  // props, so these raise a flag in the URL that the board picks up. The
  // optional callbacks still win if a parent ever does pass them.
  // The board is what opens these dialogs, so the flag has to land on the
  // board - which is not necessarily where you are standing.
  const requestShare = () => {
    setMobileOpen(false);
    if (onShareAccount) return onShareAccount();
    const query = new URLSearchParams(Object.fromEntries(searchParams));
    query.set("account", String(selectedAccount));
    query.set("share", "1");
    navigate(`/tasks/dashboard?${query.toString()}`);
  };

  const requestNewFolder = () => {
    setMobileOpen(false);
    if (onCreateFolder) {
      return onCreateFolder({ owner_id: selectedAccount, parent_id: 0 });
    }
    const query = new URLSearchParams(Object.fromEntries(searchParams));
    query.set("account", String(selectedAccount));
    query.set("newfolder", String(selectedFolder || "0"));
    navigate(`/tasks/dashboard?${query.toString()}`);
  };

  /* ---------------- Styling ------------------------------------------- */
  const menuItemStyles = {
    button: ({ active }) => ({
      height: 42,
      borderRadius: 10,
      margin: "2px 8px",
      color: active ? ui.accent : ui.textSecondary,
      backgroundColor: active ? alpha(ui.accent, 0.14) : "transparent",
      fontWeight: active ? 700 : 500,
      "&:hover": {
        backgroundColor: alpha(ui.accent, 0.1),
        color: ui.accent,
      },
    }),
    label: { fontSize: 13.5 },
    subMenuContent: { backgroundColor: "transparent" },
  };

  const sectionLabel = (text, action) => (
    <Box
      sx={{
        px: 2.5,
        pt: 2,
        pb: 0.75,
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Typography
        sx={{
          flex: 1,
          letterSpacing: 0.9,
          textTransform: "uppercase",
          fontWeight: 700,
          fontSize: 10.5,
          color: ui.textSecondary,
        }}
      >
        {text}
      </Typography>
      {action}
    </Box>
  );

  /**
   * One account row. The permission badge is the whole point of the sidebar:
   * it says what you may do before you click in and find a control missing.
   */
  const renderAccount = (account, own = false) => {
    const active = String(account.owner_id) === String(selectedAccount);
    const meta = PERMISSION_META[account.permission] || PERMISSION_META.view;
    const PermissionIcon = meta.icon;

    return (
      <MenuItem
        key={account.owner_id}
        active={active}
        onClick={() => selectAccount(account.owner_id)}
        icon={
          <Avatar
            src={
              account.owner_image
                ? `${STAFF_IMAGE_BASE}${account.owner_image}`
                : ""
            }
            sx={{
              width: 26,
              height: 26,
              fontSize: 11,
              fontWeight: 700,
              bgcolor: active ? alpha(ui.accent, 0.3) : ui.surfaceAlt,
              color: ui.textPrimary,
            }}
          >
            {initials(account.owner_name)}
          </Avatar>
        }
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              noWrap
              sx={{ fontSize: 13.5, fontWeight: active ? 700 : 600 }}
            >
              {own ? "My tasks" : account.owner_name}
            </Typography>
            {!own && (
              <Typography
                noWrap
                sx={{ fontSize: 10.5, color: ui.textSecondary }}
              >
                {meta.label}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {!own && (
              <Tooltip title={meta.label}>
                <PermissionIcon sx={{ fontSize: 14, color: ui.textSecondary }} />
              </Tooltip>
            )}
            {Number(account.open_task_count) > 0 && (
              <Chip
                size="small"
                label={account.open_task_count}
                sx={{
                  height: 18,
                  minWidth: 22,
                  fontSize: 10,
                  fontWeight: 700,
                  color: ui.accent,
                  bgcolor: alpha(ui.accent, 0.16),
                }}
              />
            )}
          </Box>
        </Box>
      </MenuItem>
    );
  };

  /**
   * Folders nest, so one with children becomes a SubMenu and one without
   * becomes a MenuItem. Both are selectable in their own right.
   */
  const renderFolder = (folder) => {
    const active = String(folder.id) === String(selectedFolder);
    const count = Number(folder.open_task_count ?? folder.task_count ?? 0);

    const label = (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography noWrap sx={{ fontSize: 13.5 }}>
          {folder.name}
        </Typography>
        {count > 0 && (
          <Chip
            size="small"
            label={count}
            sx={{
              height: 18,
              minWidth: 22,
              fontSize: 10,
              fontWeight: 700,
              color: ui.textSecondary,
              bgcolor: alpha(ui.textSecondary, 0.16),
            }}
          />
        )}
      </Box>
    );

    if (folder.children?.length) {
      return (
        <SubMenu
          key={folder.id}
          label={label}
          defaultOpen
          icon={
            <FolderOpenOutlinedIcon
              sx={{
                fontSize: 19,
                color: folder.color || (active ? ui.accent : undefined),
              }}
            />
          }
        >
          <MenuItem
            active={active}
            onClick={() => selectFolder(folder.id)}
            icon={<InboxOutlinedIcon sx={{ fontSize: 17 }} />}
          >
            <Typography sx={{ fontSize: 12.5, fontStyle: "italic" }}>
              In {folder.name}
            </Typography>
          </MenuItem>
          {folder.children.map(renderFolder)}
        </SubMenu>
      );
    }

    return (
      <MenuItem
        key={folder.id}
        active={active}
        onClick={() => selectFolder(folder.id)}
        icon={
          <FolderOutlinedIcon
            sx={{
              fontSize: 19,
              color: folder.color || (active ? ui.accent : undefined),
            }}
          />
        }
      >
        {label}
      </MenuItem>
    );
  };

  /* ------------------------------------------------------------------ */

  const content = (
    <Sidebar
      collapsed={isMobile ? false : isCollapsed}
      width="290px"
      collapsedWidth="82px"
      backgroundColor={ui.surface}
      rootStyles={{
        borderRight: `1px solid ${ui.border}`,
        height: "100vh",
        color: ui.textPrimary,
        "& .ps-sidebar-container": {
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        },
      }}
    >
      {/* ---------------- Header ---------------- */}
      <Box
        sx={{
          px: 2,
          py: 1.75,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${ui.border}`,
        }}
      >
        {(isMobile || !isCollapsed) && (
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{ fontWeight: 800, fontSize: 16, color: ui.textPrimary }}
            >
              Task Manager
            </Typography>
            <Typography sx={{ fontSize: 11, color: ui.textSecondary }}>
              {currentAccount
                ? Number(currentAccount.is_own) === 1
                  ? "Your account"
                  : `${currentAccount.owner_name}'s account`
                : "Loading…"}
            </Typography>
          </Box>
        )}

        <IconButton
          onClick={() =>
            isMobile ? setMobileOpen(false) : setIsCollapsed((v) => !v)
          }
          aria-label={isMobile ? "Close task menu" : "Collapse the sidebar"}
          sx={{ color: ui.textSecondary }}
        >
          {isMobile ? (
            <CloseIcon />
          ) : isCollapsed ? (
            <MenuOutlinedIcon />
          ) : (
            <MenuOpenOutlinedIcon />
          )}
        </IconButton>
      </Box>

      <Menu menuItemStyles={menuItemStyles} closeOnClick={false}>
        {/* ---------------- My account ---------------- */}
        {!isCollapsed && sectionLabel("My account")}

        {loadingAccounts ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 3 }}>
            <CircularProgress size={20} sx={{ color: ui.accent }} />
          </Box>
        ) : myAccount ? (
          renderAccount(myAccount, true)
        ) : (
          <MenuItem icon={<PersonOutlineOutlinedIcon />} onClick={loadAccounts}>
            <Typography sx={{ fontSize: 12.5 }}>
              Could not load — retry
            </Typography>
          </MenuItem>
        )}

        {/* ---------------- Folders of the open account ---------------- */}
        {!isCollapsed && (
          <Box
            sx={{
              px: 2.5,
              pt: 2,
              pb: 0.75,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                letterSpacing: 0.9,
                textTransform: "uppercase",
                fontWeight: 700,
                fontSize: 10.5,
                color: ui.textSecondary,
              }}
            >
              Folders
            </Typography>

            {canCreateFolder && (
              <Tooltip title="New folder">
                <IconButton
                  size="small"
                  onClick={requestNewFolder}
                  sx={{ color: ui.accent, p: 0.25 }}
                >
                  <CreateNewFolderOutlinedIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}

        <MenuItem
          active={!selectedFolder}
          onClick={() => selectFolder("")}
          icon={<InboxOutlinedIcon sx={{ fontSize: 19 }} />}
        >
          <Typography sx={{ fontSize: 13.5 }}>All tasks</Typography>
        </MenuItem>

        {loadingFolders ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 2 }}>
            <CircularProgress size={16} sx={{ color: ui.accent }} />
          </Box>
        ) : tree.length === 0 ? (
          !isCollapsed && (
            <Typography
              sx={{
                px: 2.5,
                py: 1,
                fontSize: 12,
                fontStyle: "italic",
                color: ui.textSecondary,
              }}
            >
              No folders yet
            </Typography>
          )
        ) : (
          tree.map(renderFolder)
        )}

        {/* ---------------- Shared with me ---------------- */}
        {!isCollapsed && sectionLabel("Shared with me")}

        {sharedAccounts.length === 0 ? (
          !isCollapsed && (
            <Typography
              sx={{
                px: 2.5,
                py: 1,
                fontSize: 12,
                fontStyle: "italic",
                color: ui.textSecondary,
              }}
            >
              Nobody has shared their tasks with you
            </Typography>
          )
        ) : (
          sharedAccounts.map((a) => renderAccount(a, false))
        )}

        {/* ---------------- Who I shared with ---------------- */}
        {(isMobile || !isCollapsed) &&
          sectionLabel(
            "Who I shared with",
            <Tooltip title="Give a staff member access">
              <IconButton
                size="small"
                onClick={requestShare}
                aria-label="Give a staff member access"
                sx={{
                  width: 22,
                  height: 22,
                  color: "#fff",
                  background: ui.gradient,
                  "&:hover": { background: ui.gradient, filter: "brightness(1.1)" },
                }}
              >
                <AddIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}

        {(isMobile || !isCollapsed) &&
          (loadingGrantees ? (
            <Box sx={{ display: "grid", placeItems: "center", py: 2 }}>
              <CircularProgress size={18} sx={{ color: ui.accent }} />
            </Box>
          ) : grantees.length === 0 ? (
            <Typography
              sx={{
                px: 2.5,
                py: 1,
                fontSize: 12,
                fontStyle: "italic",
                color: ui.textSecondary,
              }}
            >
              You have not given anyone access yet
            </Typography>
          ) : (
            grantees.map((g) => {
              const meta = PERMISSION_META[g.permission] || PERMISSION_META.view;
              return (
                <Box
                  key={g.id}
                  onClick={requestShare}
                  title={`Change what ${g.grantee_name || "they"} can do`}
                  sx={{
                    px: 2.5,
                    py: 0.9,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    cursor: "pointer",
                    "&:hover": { bgcolor: alpha(ui.accent, 0.08) },
                  }}
                >
                  <Avatar
                    src={
                      g.grantee_image
                        ? `${STAFF_IMAGE_BASE}${g.grantee_image}`
                        : undefined
                    }
                    sx={{ width: 28, height: 28, fontSize: 11 }}
                  >
                    {initials(g.grantee_name)}
                  </Avatar>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{ fontSize: 13, color: ui.textPrimary }}
                      noWrap
                    >
                      {g.grantee_name || "Unknown"}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 10.5, color: ui.textSecondary }}
                      noWrap
                    >
                      {meta.label}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          ))}

        <Divider sx={{ borderColor: ui.border, my: 1.5 }} />

        {/* ---------------- Actions ---------------- */}
        <MenuItem
          onClick={() => {
            loadAccounts();
            loadFolders();
            loadGrantees();
          }}
          icon={<RefreshOutlinedIcon sx={{ fontSize: 19 }} />}
        >
          <Typography sx={{ fontSize: 13.5 }}>Refresh</Typography>
        </MenuItem>

        <MenuItem
          onClick={() => navigate("/admin/dashboard")}
          icon={<ArrowBackIcon sx={{ fontSize: 19 }} />}
        >
          <Typography sx={{ fontSize: 13.5 }}>Back to dashboard</Typography>
        </MenuItem>
      </Menu>

      {/* ---------------- Footer: what you may do here ---------------- */}
      {!isCollapsed && currentAccount && (
        <Box
          sx={{
            mt: "auto",
            p: 2,
            borderTop: `1px solid ${ui.border}`,
            bgcolor: alpha(ui.accent, 0.06),
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <GroupOutlinedIcon sx={{ fontSize: 16, color: ui.accent }} />
            <Typography sx={{ fontSize: 11.5, color: ui.textSecondary }}>
              {Number(currentAccount.is_own) === 1
                ? "Full rights on your own account"
                : PERMISSION_META[currentAccount.permission]?.label ||
                  "View only"}
            </Typography>
          </Box>
        </Box>
      )}
    </Sidebar>
  );

  /**
   * On a phone the sidebar is a drawer over the board rather than a column
   * beside it - there is not room for both. It renders the SAME tree, never
   * collapsed, and closes itself as soon as you pick something, because on
   * mobile choosing an account or folder means you want to look at it.
   */
  if (isMobile) {
    return (
      <>
        {/* Bottom-left, not top-left: the TopBar logo already owns that
            corner, and a fixed button there would sit on top of it. */}
        <IconButton
          onClick={() => setMobileOpen(true)}
          aria-label="Open task menu"
          sx={{
            position: "fixed",
            bottom: 20,
            left: 16,
            zIndex: (t) => t.zIndex.drawer - 1,
            width: 52,
            height: 52,
            background: ui.gradient,
            color: "#fff",
            boxShadow: "0 8px 20px -6px rgba(0,0,0,0.45)",
            "&:hover": { background: ui.gradient, filter: "brightness(1.08)" },
          }}
        >
          <MenuOutlinedIcon />
        </IconButton>

        <Drawer
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          // Kept mounted so the accounts and folders are not refetched
          // every time the drawer is opened.
          ModalProps={{ keepMounted: true }}
          PaperProps={{ sx: { width: 290, border: "none" } }}
        >
          {content}
        </Drawer>
      </>
    );
  }

  return content;
};

export default TaskSideBar;
